/**
 * AudioManager - Centralized audio management system
 * Uses procedural synthesis via Web Audio API for all game sounds.
 */

import Phaser from 'phaser';
import { SynthAudio } from './SynthAudio';

/**
 * Configuration for audio settings
 */
export interface AudioConfig {
  /** Background music volume (0-1) */
  musicVolume: number;
  /** Sound effects volume (0-1) */
  sfxVolume: number;
  /** Master mute toggle */
  muted: boolean;
}

const DEFAULT_AUDIO_CONFIG: AudioConfig = {
  musicVolume: 0.3,
  sfxVolume: 0.6,
  muted: false,
};

const STORAGE_KEY = 'zenith_audio';

/**
 * Centralized audio management for the game
 * Uses synthesized audio instead of pre-recorded files
 */
export class AudioManager {
  private scene: Phaser.Scene;
  private config: AudioConfig;
  private synth: SynthAudio;
  private slideStopFn: (() => void) | null = null;
  private chimeTimer: Phaser.Time.TimerEvent | null = null;
  private isDestroyed: boolean = false;

  constructor(scene: Phaser.Scene, config?: Partial<AudioConfig>) {
    this.scene = scene;
    this.config = { ...DEFAULT_AUDIO_CONFIG, ...config };
    this.synth = new SynthAudio();
    this.loadConfig();
  }

  /**
   * Preload audio assets (no-op for synth audio)
   */
  preload(): void {
    // No assets to preload - all audio is synthesized
  }

  /**
   * Initialize audio system
   */
  create(): void {
    // Initialize on first user interaction
    this.scene.input.once('pointerdown', () => {
      this.initAudio();
    });

    // Also try to init immediately in case context is allowed
    this.initAudio();
  }

  /**
   * Initialize the audio context
   */
  private initAudio(): void {
    if (this.isDestroyed) return;

    const initialized = this.synth.init();
    if (initialized) {
      this.synth.resume();
      this.synth.setMusicVolume(this.config.musicVolume);
      this.synth.setSFXVolume(this.config.sfxVolume);
      this.synth.setMuted(this.config.muted);

      if (!this.config.muted) {
        this.startAmbient();
      }
    }
  }

  /**
   * Start ambient audio layers
   */
  private startAmbient(): void {
    if (this.config.muted || this.isDestroyed) return;

    this.synth.startAmbient();
    this.scheduleChimes();
  }

  /**
   * Stop ambient audio layers
   */
  private stopAmbient(): void {
    this.synth.stopAmbient();

    if (this.chimeTimer) {
      this.chimeTimer.remove();
      this.chimeTimer = null;
    }
  }

  /**
   * Schedule random chime sounds
   */
  private scheduleChimes(): void {
    if (this.config.muted || this.isDestroyed) return;

    const delay = Phaser.Math.Between(8000, 15000);

    this.chimeTimer = this.scene.time.delayedCall(delay, () => {
      if (!this.isDestroyed && !this.config.muted) {
        this.synth.playChime();
        this.scheduleChimes();
      }
    });
  }

  // ============= SFX Methods =============

  /**
   * Play impact sound when piece collides
   */
  playImpact(): void {
    if (this.config.muted) return;
    this.synth.playImpact();
  }

  /**
   * Start looping slide sound
   * @returns Dummy sound object for compatibility
   */
  playSlideStart(): Phaser.Sound.BaseSound | null {
    if (this.config.muted) return null;
    this.slideStopFn = this.synth.playSlideStart();
    // Return null since we don't use Phaser sound
    return null;
  }

  /**
   * Stop the slide sound
   */
  playSlideStop(_sound: Phaser.Sound.BaseSound | null): void {
    if (this.slideStopFn) {
      this.slideStopFn();
      this.slideStopFn = null;
    }
  }

  /**
   * Play lock/release sound
   */
  playLock(): void {
    if (this.config.muted) return;
    this.synth.playLock();
  }

  /**
   * Play spawn sound
   */
  playSpawn(): void {
    if (this.config.muted) return;
    this.synth.playSpawn();
  }

  /**
   * Play falling/whoosh sound
   */
  playFall(): void {
    if (this.config.muted) return;
    this.synth.playFall();
  }

  /**
   * Play win celebration sound
   */
  playWin(): void {
    if (this.config.muted) return;
    this.synth.playWin();
  }

  /**
   * Play button tap sound
   */
  playButtonTap(): void {
    if (this.config.muted) return;
    this.synth.playButtonTap();
  }

  // ============= Volume Controls =============

  /**
   * Set music/ambient volume
   */
  setMusicVolume(volume: number): void {
    this.config.musicVolume = Math.max(0, Math.min(1, volume));
    this.synth.setMusicVolume(this.config.musicVolume);
    this.saveConfig();
  }

  /**
   * Get current music volume
   */
  getMusicVolume(): number {
    return this.config.musicVolume;
  }

  /**
   * Set SFX volume
   */
  setSFXVolume(volume: number): void {
    this.config.sfxVolume = Math.max(0, Math.min(1, volume));
    this.synth.setSFXVolume(this.config.sfxVolume);
    this.saveConfig();
  }

  /**
   * Get current SFX volume
   */
  getSFXVolume(): number {
    return this.config.sfxVolume;
  }

  /**
   * Set muted state
   */
  setMuted(muted: boolean): void {
    this.config.muted = muted;
    this.synth.setMuted(muted);

    if (muted) {
      this.stopAmbient();
    } else {
      this.startAmbient();
    }

    this.saveConfig();
  }

  /**
   * Get muted state
   */
  isMuted(): boolean {
    return this.config.muted;
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    this.setMuted(!this.config.muted);
    return this.config.muted;
  }

  // ============= Persistence =============

  /**
   * Load config from localStorage
   */
  private loadConfig(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.config = { ...DEFAULT_AUDIO_CONFIG, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load audio config:', e);
    }
  }

  /**
   * Save config to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
    } catch (e) {
      console.warn('Failed to save audio config:', e);
    }
  }

  /**
   * Get current config
   */
  getConfig(): AudioConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.isDestroyed = true;
    this.stopAmbient();
    this.synth.destroy();
  }
}
